import { toast } from "sonner";
import { requestUploads } from "./api";
import { ImageValidationError, MASTER_IMAGE_MAX_BYTES, validateImage } from "./imageValidation";

export type UploadKind = "brick" | "thumbnail";

export interface SignedUpload {
  key: string;
  url: string;
  fields: Record<string, string>;
}

export const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp";

export const postUpload = (
  upload: SignedUpload,
  file: Blob,
  onProgress?: (percent: number) => void,
) => {
  const form = new FormData();
  Object.entries(upload.fields).forEach(([name, value]) => form.append(name, value));
  form.append("file", file);
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", upload.url);
    xhr.upload.onprogress = (ev) => onProgress?.((ev.loaded / ev.total) * 100);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(xhr.responseText || `Uploaden mislukt (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Uploaden mislukt"));
    xhr.send(form);
  });
};

export const uploadImage = async (
  organization: string,
  series: string,
  kind: UploadKind,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<string> => {
  const contentType = await validateImage(file);
  const [upload] = await requestUploads(organization, series, [{ kind, contentType }]);
  await postUpload(upload, file, onProgress);
  return upload.key;
};

export const uploadBrickRenders = async (
  organization: string,
  series: string,
  blobs: Blob[],
): Promise<string[]> => {
  const uploads: SignedUpload[] = await requestUploads(
    organization,
    series,
    blobs.map(() => ({ kind: "brick", contentType: "image/png" })),
  );
  await Promise.all(uploads.map((upload, i) => postUpload(upload, blobs[i])));
  return uploads.map((upload) => upload.key);
};

// Checks picked or dropped files and reports each invalid one, so only valid files are uploaded.
const keepValid = async (files: File[], maxBytes?: number) => {
  const checked = await Promise.all(
    files.map(async (file) => {
      try {
        await validateImage(file, maxBytes);
        return file;
      } catch (e) {
        if (!(e instanceof ImageValidationError)) throw e;
        toast.error(`${file.name}: ${e.message}`);
        return null;
      }
    }),
  );
  return checked.filter((file): file is File => file != null);
};

export const keepValidImages = (files: File[]) => keepValid(files);

export const keepValidMasterImages = (files: File[]) => keepValid(files, MASTER_IMAGE_MAX_BYTES);
